import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames/bind';
import * as styles from './Loading.css';

const cx = classnames.bind(styles);

export enum LoadingSize {
    EXTRA_SMALL = 'extra-small',
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    EXTRA_LARGE = 'extra-large'
}

const LoadingDimension = {
    [LoadingSize.EXTRA_SMALL]: 12,
    [LoadingSize.SMALL]: 16,
    [LoadingSize.MEDIUM]: 20,
    [LoadingSize.LARGE]: 32,
    [LoadingSize.EXTRA_LARGE]: 60
} as const;

const SIZE_CLASSES = {
    [LoadingSize.EXTRA_SMALL]: 'size-xs',
    [LoadingSize.SMALL]: 'size-sm',
    [LoadingSize.MEDIUM]: 'size-md',
    [LoadingSize.LARGE]: 'size-lg',
    [LoadingSize.EXTRA_LARGE]: 'size-xl'
} as const;

export interface Props extends ComponentProps<'svg'> {
    testId?: string;
    className?: string;
    size?: LoadingSize;
    inline?: boolean;
    title?: string;
}

export function Loading({
    testId,
    className,
    size = LoadingSize.LARGE,
    inline = false,
    title,
    ...props
}: Props) {
    const { t } = useTranslation();
    const classes = cx('base', SIZE_CLASSES[size], { inline }, className);

    return (
        <svg
            role="status"
            data-testid={testId}
            className={classes}
            viewBox="0 0 50 50"
            width={LoadingDimension[size]}
            height={LoadingDimension[size]}
            {...props}>
            <title>{title || t('common:loading')}</title>
            <g>
                <circle cx="25" cy="25" r="20" />
            </g>
        </svg>
    );
}
